import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import styled from 'styled-components';
import theme from '../theme';
import { TerminalCard, TerminalButton, TerminalInput, TerminalTitle, TerminalText } from './shared/TerminalBox';
import { TerminalSelect, TerminalLabel, TerminalAlert, TerminalLoading, TerminalCheckbox, TerminalCheckboxLabel } from './shared/TerminalForms';


const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
`;

const Title = styled(TerminalTitle)`
  margin-bottom: 2rem;
  font-size: 2rem;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const HelperText = styled.small`
  display: block;
  margin-top: 0.25rem;
  color: ${theme.colors.textMuted};
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
`;

const ChartCard = styled(TerminalCard)`
  margin-top: 2rem;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const ChartTitle = styled.h2`
  font-family: ${theme.fonts.mono};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '[ ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' ]';
    color: ${theme.colors.primary};
  }
`;

const ChartInfo = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text};
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 500px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.bgSecondary};
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${theme.colors.bgTertiary};
  border: 1px solid ${theme.colors.borderBright};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.glow};
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 0.25rem;
`;

const SearchResult = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid ${theme.colors.border};
  transition: background-color ${theme.transitions.fast};
  font-family: ${theme.fonts.mono};
  
  &:hover {
    background: ${theme.colors.overlayLight};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SearchResultSymbol = styled.div`
  font-weight: 700;
  color: ${theme.colors.primary};
  font-size: 0.875rem;
`;

const SearchResultName = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.textSecondary};
  margin-top: 0.25rem;
`;

const LatestData = styled.div`
  margin-top: 1.5rem;
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  color: ${theme.colors.textSecondary};
`;

const LatestDataTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${theme.colors.text};
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 1px;
`;

const LatestDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const LatestDataItem = styled.div`
  color: ${theme.colors.text};
  font-size: 0.875rem;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const StatusMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  text-align: center;
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  border: 1px solid;
  
  &.warning {
    background: rgba(255, 170, 0, 0.1);
    color: ${theme.colors.warning};
    border-color: ${theme.colors.warning};
  }
  
  &.success {
    background: rgba(0, 255, 65, 0.1);
    color: ${theme.colors.success};
    border-color: ${theme.colors.success};
  }
  
  &.info {
    background: rgba(0, 255, 65, 0.1);
    color: ${theme.colors.text};
    border-color: ${theme.colors.border};
  }
`;

const OHLCV = () => {
  const [symbol, setSymbol] = useState('NASDAQ:AAPL');
  const [barType, setBarType] = useState('day');
  const [barInterval, setBarInterval] = useState(1);
  const [extended, setExtended] = useState(true);
  const [badj, setBadj] = useState(true);
  const [dadj, setDadj] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const canvasRef = useRef(null);

  const barTypes = [
    { value: 'tick', label: 'Tick' },
    { value: 'second', label: 'Second' },
    { value: 'minute', label: 'Minute' },
    { value: 'hour', label: 'Hour' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' }
  ];

  const getBarIntervalHelp = () => {
    switch (barType) {
      case 'tick': return 'Number of ticks to group together';
      case 'second': return 'Number of seconds to group together (e.g., 5 = 5-second bars)';
      case 'minute': return 'Number of minutes to group together (e.g., 5 = 5-minute bars)';
      case 'hour': return 'Number of hours to group together (e.g., 4 = 4-hour bars)';
      case 'day': return 'Number of days to group together (e.g., 1 = daily bars)';
      case 'week': return 'Number of weeks to group together (e.g., 1 = weekly bars)';
      case 'month': return 'Number of months to group together (e.g., 1 = monthly bars)';
      default: return 'Number of time units to group together';
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/ohlcv/${symbol}',
        {
          params: {
            bar_type: barType,
            bar_interval: barInterval,
            extended: extended,
            badj: badj,
            dadj: dadj
          }
        }
      );

      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const searchSymbols = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await api.get('/symbols/search',
        {
          params: { query }
        }
      );
      setSearchResults(response.data.data || []);
    } catch (err) {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [symbol, barType, barInterval, extended, badj, dadj]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchSymbols(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const drawChart = () => {
    if (!data || !data.series || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const series = data.series;

    if (series.length === 0) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const width = canvas.width;
    const height = canvas.height;
    const padding = 60;

    ctx.clearRect(0, 0, width, height);

    let minPrice = Math.min(...series.map(d => d.low));
    let maxPrice = Math.max(...series.map(d => d.high));
    const priceRange = maxPrice - minPrice;
    const pricePadding = priceRange * 0.1;
    minPrice -= pricePadding;
    maxPrice += pricePadding;

    let maxVolume = Math.max(...series.map(d => d.volume));

    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Draw grid with terminal colors
    ctx.strokeStyle = theme.colors.border;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    for (let i = 0; i <= 5; i++) {
      const y = padding + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    for (let i = 0; i <= 10; i++) {
      const x = padding + (chartWidth / 10) * i;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }

    ctx.setLineDash([]);

    // Draw price labels with terminal colors
    ctx.fillStyle = theme.colors.textMuted;
    ctx.font = `12px ${theme.fonts.mono}`;
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const price = maxPrice - (priceRange / 5) * i;
      const y = padding + (chartHeight / 5) * i;
      ctx.fillText(price.toFixed(2), padding - 10, y + 4);
    }

    if (series.length === 1) {
      const point = series[0];
      const x = padding + chartWidth / 2;
      const closeY = padding + ((maxPrice - point.close) / priceRange) * chartHeight;
      const openY = padding + ((maxPrice - point.open) / priceRange) * chartHeight;
      const highY = padding + ((maxPrice - point.high) / priceRange) * chartHeight;
      const lowY = padding + ((maxPrice - point.low) / priceRange) * chartHeight;

      const isGreen = point.close >= point.open;
      const candleColor = isGreen ? theme.colors.success : theme.colors.error;

      ctx.strokeStyle = candleColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, highY);
      ctx.lineTo(x, lowY);
      ctx.stroke();

      const bodyHeight = Math.max(2, Math.abs(closeY - openY));
      const bodyY = Math.min(openY, closeY);
      ctx.fillStyle = candleColor;
      ctx.fillRect(x - 15, bodyY, 30, bodyHeight);

      const volumeHeight = 80;
      const volumeY = height - volumeHeight - padding / 2;
      const volumeMax = maxVolume * 1.2;
      const barHeight = (point.volume / volumeMax) * volumeHeight;
      
      ctx.fillStyle = theme.colors.primary;
      ctx.fillRect(x - 20, volumeY + volumeHeight - barHeight, 40, barHeight);

      ctx.fillStyle = theme.colors.textMuted;
      ctx.font = `12px ${theme.fonts.mono}`;
      ctx.textAlign = 'center';
      ctx.fillText(`H: $${point.high.toFixed(2)}`, x, highY - 10);
      ctx.fillText(`L: $${point.low.toFixed(2)}`, x, lowY + 20);
      ctx.fillText(`O: $${point.open.toFixed(2)}`, x - 50, openY);
      ctx.fillText(`C: $${point.close.toFixed(2)}`, x + 50, closeY);

    } else {
      const pointSpacing = chartWidth / (series.length - 1);

      // Draw price line with terminal green
      ctx.strokeStyle = theme.colors.primary;
      ctx.lineWidth = 2;
      ctx.beginPath();

      series.forEach((point, index) => {
        const x = padding + index * pointSpacing;
        const y = padding + ((maxPrice - point.close) / priceRange) * chartHeight;
        
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();

      // Draw data points
      ctx.fillStyle = theme.colors.primary;
      series.forEach((point, index) => {
        const x = padding + index * pointSpacing;
        const y = padding + ((maxPrice - point.close) / priceRange) * chartHeight;
        
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, 2 * Math.PI);
        ctx.fill();
      });

      // Draw volume bars
      const volumeHeight = 80;
      const volumeY = height - volumeHeight - padding / 2;
      const volumeMax = maxVolume * 1.2;

      ctx.fillStyle = theme.colors.success;
      series.forEach((point, index) => {
        const x = padding + index * pointSpacing;
        const barWidth = Math.max(1, pointSpacing * 0.8);
        const barHeight = (point.volume / volumeMax) * volumeHeight;
        ctx.fillRect(x - barWidth / 2, volumeY + volumeHeight - barHeight, barWidth, barHeight);
      });

      // Draw time labels
      const labelInterval = Math.max(1, Math.floor(series.length / 5));
      ctx.fillStyle = theme.colors.textMuted;
      ctx.font = `10px ${theme.fonts.mono}`;
      ctx.textAlign = 'center';
      
      for (let i = 0; i < series.length; i += labelInterval) {
        const x = padding + i * pointSpacing;
        const time = new Date(series[i].time * 1000);
        const timeLabel = time.toLocaleDateString([], { month: 'short', day: 'numeric' });
        
        ctx.fillText(timeLabel, x, height - padding / 2);
      }
    }

    // Draw volume label
    ctx.fillStyle = theme.colors.textMuted;
    ctx.font = `12px ${theme.fonts.mono}`;
    ctx.textAlign = 'left';
    ctx.fillText('Volume', padding, height - padding / 2 + 20);
  };

  useEffect(() => {
    drawChart();
  }, [data]);

  useEffect(() => {
    const handleResize = () => {
      drawChart();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  return (
    <Container>
      <Title>Real-Time OHLCV Charts</Title>

      <TerminalCard>
        <FormGrid>
          <FormGroup>
            <TerminalLabel>Symbol</TerminalLabel>
            <SearchContainer>
              <TerminalInput
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                placeholder="NASDAQ:AAPL, BINANCE:BTCUSDT"
                onFocus={() => setShowSearch(true)}
                onBlur={() => setTimeout(() => setShowSearch(false), 200)}
              />
              {showSearch && (
                <SearchResults>
                  <TerminalInput
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search symbols..."
                    style={{ marginBottom: '0.5rem', border: 'none', borderBottom: `1px solid ${theme.colors.border}` }}
                  />
                  {searchResults.map((result, index) => (
                    <SearchResult
                      key={index}
                      onClick={() => {
                        setSymbol(result.symbol_code);
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                    >
                      <SearchResultSymbol>{result.symbol_code}</SearchResultSymbol>
                      <SearchResultName>{result.name}</SearchResultName>
                    </SearchResult>
                  ))}
                </SearchResults>
              )}
            </SearchContainer>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Bar Type</TerminalLabel>
            <TerminalSelect
              value={barType}
              onChange={(e) => setBarType(e.target.value)}
            >
              {barTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </TerminalSelect>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Bar Interval</TerminalLabel>
            <TerminalInput
              type="number"
              value={barInterval}
              onChange={(e) => setBarInterval(parseInt(e.target.value) || 1)}
              min="1"
            />
            <HelperText>{getBarIntervalHelp()}</HelperText>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Options</TerminalLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <TerminalCheckboxLabel>
                <TerminalCheckbox
                  type="checkbox"
                  checked={extended}
                  onChange={(e) => setExtended(e.target.checked)}
                />
                Extended Hours
              </TerminalCheckboxLabel>
              <TerminalCheckboxLabel>
                <TerminalCheckbox
                  type="checkbox"
                  checked={badj}
                  onChange={(e) => setBadj(e.target.checked)}
                />
                Back Adjustment
              </TerminalCheckboxLabel>
              <TerminalCheckboxLabel>
                <TerminalCheckbox
                  type="checkbox"
                  checked={dadj}
                  onChange={(e) => setDadj(e.target.checked)}
                />
                Dividend Adjustment
              </TerminalCheckboxLabel>
            </div>
          </FormGroup>
        </FormGrid>
      </TerminalCard>

      {error && (
        <TerminalAlert className="error" style={{ marginTop: '1rem' }}>
          {error}
        </TerminalAlert>
      )}

      {loading && (
        <TerminalLoading>
          Loading OHLCV data...
        </TerminalLoading>
      )}

      {data && (
        <ChartCard>
          <ChartHeader>
            <ChartTitle>
              {data.code} - {barType.charAt(0).toUpperCase() + barType.slice(1)} Chart
            </ChartTitle>
            <ChartInfo>
              <InfoItem>
                Update: {data.last_update ? formatTimestamp(data.last_update) : 'N/A'}
              </InfoItem>
              <InfoItem>
                Points: {data.series ? data.series.length : 0}
              </InfoItem>
              <InfoItem>
                Type: {data.bar_type || barType}
              </InfoItem>
            </ChartInfo>
          </ChartHeader>

          <Canvas ref={canvasRef} />

          {data && data.series && data.series.length > 0 && (
            <LatestData>
              <LatestDataTitle>Latest Data</LatestDataTitle>
              <LatestDataGrid>
                <LatestDataItem>Open: ${data.series[data.series.length - 1].open?.toFixed(2)}</LatestDataItem>
                <LatestDataItem>High: ${data.series[data.series.length - 1].high?.toFixed(2)}</LatestDataItem>
                <LatestDataItem>Low: ${data.series[data.series.length - 1].low?.toFixed(2)}</LatestDataItem>
                <LatestDataItem>Close: ${data.series[data.series.length - 1].close?.toFixed(2)}</LatestDataItem>
                <LatestDataItem>Volume: {data.series[data.series.length - 1].volume?.toLocaleString()}</LatestDataItem>
              </LatestDataGrid>
            </LatestData>
          )}

          {data && data.series && data.series.length === 0 && (
            <StatusMessage className="info">
              [NO DATA] No OHLCV data found for {symbol} with current parameters.
            </StatusMessage>
          )}

          {data && data.series && data.series.length === 1 && (
            <StatusMessage className="warning">
              [LIMITED DATA] Only 1 data point available. Try Day with interval 1, or Minute with interval 5 for more data points.
            </StatusMessage>
          )}

          {data && data.series && data.series.length > 1 && (
            <StatusMessage className="success">
              [DATA LOADED] {data.series.length} data points loaded. Line chart showing price trends over time.
            </StatusMessage>
          )}
        </ChartCard>
      )}
    </Container>
  );
};

export default OHLCV;
